export const dividerColor = 'rgba(255,255,255,0.08)';
export const DEFAULT_SCORE_MIN = 0;
export const DEFAULT_SCORE_MAX = 115;

export const DIMENSION_META = {
  start: { labelKey: 'fs_dim_start_label', defaultWeight: 10 },
  initial_depth: { labelKey: 'fs_dim_initial_depth_label', defaultWeight: 10 },
  stationary: { labelKey: 'fs_dim_stationary_label', defaultWeight: 15 },
  alarm: { labelKey: 'fs_dim_alarm_label', defaultWeight: 35 },
  depth_move: { labelKey: 'fs_dim_depth_move_label', defaultWeight: 10 },
  activity: { labelKey: 'fs_dim_activity_label', defaultWeight: 10 },
  oxygen: { labelKey: 'fs_dim_oxygen_label', defaultWeight: 25 },
  current: { labelKey: 'fs_dim_current_label', defaultWeight: 10 },
  stop: { labelKey: 'fs_dim_stop_label', defaultWeight: 20 },
};

const SCORE_GREEN_STOP = 100;
const SCORE_COLOR_STOPS = [
  { score: 0, rgb: [255, 0, 0] },
  { score: 60, rgb: [200, 200, 0] },
  { score: SCORE_GREEN_STOP, rgb: [90, 255, 90] },
];
const SCORE_OVERSCORE_RGB = [232, 255, 226];
const ATTENTION_COLOR_STOPS = [
  { value: 0, rgb: [255, 0, 0] },
  { value: 50, rgb: [200, 200, 0] },
  { value: 80, rgb: [90, 255, 90] },
  { value: 100, rgb: [90, 255, 90] },
];

export const translateOrFallback = (translate, key, fallback) => {
  const translated = typeof translate === 'function' ? translate(key) : key;
  return translated && translated !== key ? translated : fallback;
};

export const formatNumber = (value, digits = 2) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
export const shortKey = (key) => String(key || '').replace(/^FeederScore_/, '').replace(/^dim_/, '').replace(/_/g, ' ');

const interpolateStops = (rawValue, stops, valueKey = 'value') => {
  const value = Math.max(stops[0][valueKey], Math.min(stops[stops.length - 1][valueKey], Number(rawValue)));
  const nextIndex = stops.findIndex((stop) => value <= stop[valueKey]);
  if (nextIndex <= 0) return stops[0].rgb;

  const from = stops[nextIndex - 1];
  const to = stops[nextIndex];
  const span = to[valueKey] - from[valueKey];
  const t = span > 0 ? (value - from[valueKey]) / span : 0;
  return from.rgb.map((channel, index) => Math.round(channel + (to.rgb[index] - channel) * t ** 1.5));
};

const scoreRgb = (score, minScore = DEFAULT_SCORE_MIN, maxScore = DEFAULT_SCORE_MAX) => {
  const floor = Math.min(SCORE_GREEN_STOP, Math.max(DEFAULT_SCORE_MIN, Number(minScore) || DEFAULT_SCORE_MIN));
  const ceiling = Math.max(SCORE_GREEN_STOP + 1, Number(maxScore) || DEFAULT_SCORE_MAX);
  const stops = [
    ...SCORE_COLOR_STOPS.map((stop) => ({
      ...stop,
      score: floor + ((stop.score - DEFAULT_SCORE_MIN) / (SCORE_GREEN_STOP - DEFAULT_SCORE_MIN)) * (SCORE_GREEN_STOP - floor),
    })),
    { score: ceiling, rgb: SCORE_OVERSCORE_RGB },
  ];
  return interpolateStops(score, stops, 'score');
};

export const scoreTextColor = (score, minScore = DEFAULT_SCORE_MIN, maxScore = DEFAULT_SCORE_MAX) => {
  if (score == null) return 'rgba(166,166,166,.6)';
  const [r, g, b] = scoreRgb(score, minScore, maxScore);
  return `rgb(${r}, ${g}, ${b})`;
};

export const scoreCellBg = (score, minScore = DEFAULT_SCORE_MIN, maxScore = DEFAULT_SCORE_MAX) => {
  if (score == null) return 'transparent';
  const [r, g, b] = scoreRgb(score, minScore, maxScore);
  return `rgba(${r}, ${g}, ${b}, .13)`;
};

export const attentionTextColor = (pct) => {
  if (pct == null) return 'rgba(166,166,166,.6)';
  const [r, g, b] = interpolateStops(pct, ATTENTION_COLOR_STOPS);
  return `rgb(${r}, ${g}, ${b})`;
};

export const attentionCellBg = (pct) => {
  if (pct == null) return 'transparent';
  const [r, g, b] = interpolateStops(pct, ATTENTION_COLOR_STOPS);
  return `rgba(${r}, ${g}, ${b}, .13)`;
};

export const isMaxScore = (score, maxScore = DEFAULT_SCORE_MAX) => (
  score != null && Number(score) >= Math.max(SCORE_GREEN_STOP + 1, Number(maxScore) || DEFAULT_SCORE_MAX)
);
